import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import propTypes from 'prop-types';
import {
  ScrollingCarousel,
  ScrollingCarouselStep,
  ImageGalleryModal,
} from '@landtrustinc/design-system';
import { formatTimeAgo } from '../../../util/dates';
import { NamedLink } from '../../../components';
import BytescaleVideo from '../../../components/BytescaleVideo/BytescaleVideo';
import Avatar from '../../../components/Avatar/Avatar';
import css from './ListingUpdate.module.css';
import { createSlug } from '../../../util/urlHelpers';
import OptionMenu from '../../../components/OptionMenu/OptionMenu';
import { isVideo } from '../../../util/data';
import getBytescaleImageURL from '../../../util/bytescale';

const SectionPropertyPostUpdate = props => {
  const { listing, activity, renderBellIcon, fetchTotalLikesData, authUser } = props;
  const { title } = listing.attributes;
  const listingUrl = `/l/${createSlug(title)}/${listing.id.uuid}`;
  const slug = createSlug(title);
  const updateId = activity?.id;
  const activityPhotos = activity.photos || [];
  const hasImages = activityPhotos.length > 0;
  const carouselId = `property-post-carousel-${updateId}`;

  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Filter only images (not videos) for the gallery modal
  const galleryImages = activityPhotos
    .filter(image => !isVideo(image.path))
    .map((image, index) => ({
      src: getBytescaleImageURL(image.path, 1200, 1200),
      alt: `Property update ${index + 1} from ${title}`,
    }));

  const handleImageClick = index => {
    // Find the corresponding index in galleryImages (excluding videos)
    const imageOnlyIndex =
      activityPhotos.slice(0, index + 1).filter(img => !isVideo(img.path)).length - 1;
    if (imageOnlyIndex >= 0) {
      setSelectedIndex(imageOnlyIndex);
      setIsGalleryOpen(true);
    }
  };

  return (
    <>
      <div key={activity.createdAt} className={css.content}>
        <div>
          <div className={css.flexContainer}>
            <div className={css.avatarContainer}>
              <NamedLink name="ListingPage" params={{ id: listing.id.uuid, slug }}>
                <>
                  <Avatar
                    user={listing.author}
                    disableProfileLink
                    className={css.customAvatarSize}
                    listing={listing}
                    useListingImage
                  />
                  {renderBellIcon}
                </>
              </NamedLink>
            </div>
            <div className={css.propertyNameContainer}>
              <div className={css.propertyName}>
                <NamedLink name="ListingPage" params={{ id: listing.id.uuid, slug }}>
                  <FormattedMessage
                    id="ListingPage.activityFeedPropertyName"
                    values={{
                      propertyName: listing.attributes.title,
                    }}
                  />
                </NamedLink>
              </div>
              <div className={css.dateWrapper}>
                <span className={css.dateContainer}>
                  <p className={css.landownerContainer}>
                    <FormattedMessage
                      id="ListingPage.activityFeedLandownerUpdate"
                      values={{
                        landownerName: listing.author.attributes.profile.displayName,
                      }}
                    />
                    {' • '}
                    {formatTimeAgo(activity?.createdAt)}
                  </p>
                </span>
              </div>
            </div>
          </div>
          <div className={css.postWrapper}>
            <div className={css.descriptionStyles}>{activity.description}</div>
            {hasImages && (
              <>
                <ScrollingCarousel id={carouselId} showDots={activityPhotos.length > 1}>
                  {activityPhotos.map((image, index) => (
                    <ScrollingCarouselStep
                      key={image.path || index}
                      parentId={carouselId}
                      onClick={() => !isVideo(image.path) && handleImageClick(index)}
                    >
                      <div className={css.imageWrapper}>
                        {isVideo(image.path) ? (
                          <BytescaleVideo
                            videoPath={image.path}
                            autoPlay
                            muted
                            playsInline
                            controls
                            className={css.videoStyles}
                          />
                        ) : (
                          <img
                            src={getBytescaleImageURL(image.path, 900, 900)}
                            alt={`Property update ${index + 1} from ${title}`}
                            className={css.imageStyles}
                          />
                        )}
                      </div>
                    </ScrollingCarouselStep>
                  ))}
                </ScrollingCarousel>

                {galleryImages.length > 0 && (
                  <ImageGalleryModal
                    id={`${carouselId}-gallery`}
                    isOpen={isGalleryOpen}
                    onClose={() => setIsGalleryOpen(false)}
                    images={galleryImages}
                    initialIndex={selectedIndex}
                  />
                )}
              </>
            )}
          </div>
          <OptionMenu
            listingUrl={listingUrl}
            updateId={updateId}
            fetchTotalLikesData={fetchTotalLikesData}
            authUser={authUser}
          />
        </div>
      </div>
    </>
  );
};

SectionPropertyPostUpdate.defaultProps = {};

SectionPropertyPostUpdate.propTypes = {
  listing: propTypes.object.isRequired,
};

export default SectionPropertyPostUpdate;
